import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { calculateOriginalPrice } from '../../utils/priceComparison';

export default function ProductOptionsSheet({
  isOpen,
  onClose,
  product,
  onConfirm,
}) {
  const [selectedOptions, setSelectedOptions] = useState({});
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    if (!isOpen) return;
    const html = document.documentElement;
    const body = document.body;
    const originalHtmlOverflow = html.style.overflow;
    const originalBodyOverflow = body.style.overflow;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    return () => {
      html.style.overflow = originalHtmlOverflow;
      body.style.overflow = originalBodyOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedOptions({});
      setQuantity(1);
    }
  }, [isOpen, product?._id]);

  if (!product) return null;

  const optionGroups = product.optionGroups || [];

  const handleOptionToggle = (groupIndex, optionIndex, maxSelections) => {
    setSelectedOptions((prev) => {
      const current = prev[groupIndex];

      if (maxSelections === 1) {
        return { ...prev, [groupIndex]: optionIndex };
      }

      const currentArray = Array.isArray(current) ? current : [];
      const isSelected = currentArray.includes(optionIndex);

      if (isSelected) {
        return {
          ...prev,
          [groupIndex]: currentArray.filter((i) => i !== optionIndex),
        };
      }

      if (currentArray.length >= maxSelections) {
        return {
          ...prev,
          [groupIndex]: [...currentArray.slice(1), optionIndex],
        };
      }

      return {
        ...prev,
        [groupIndex]: [...currentArray, optionIndex],
      };
    });
  };

  const isOptionSelected = (groupIndex, optionIndex) => {
    const current = selectedOptions[groupIndex];
    if (current === undefined) return false;
    if (Array.isArray(current)) return current.includes(optionIndex);
    return current === optionIndex;
  };

  const areRequiredOptionsSelected = () => {
    return optionGroups.every((group, groupIndex) => {
      if (!group.required) return true;
      const current = selectedOptions[groupIndex];
      if (current === undefined) return false;
      if (Array.isArray(current)) {
        return current.length >= (group.minSelections || 1);
      }
      return current !== undefined && current !== null;
    });
  };

  const getOptionsPrice = () => {
    return optionGroups.reduce((sum, group, groupIndex) => {
      const current = selectedOptions[groupIndex];
      if (current === undefined) return sum;

      if (Array.isArray(current)) {
        return sum + current.reduce((s, optIdx) =>
          s + (group.options[optIdx]?.price || 0), 0);
      }
      return sum + (group.options[current]?.price || 0);
    }, 0);
  };

  const unitPrice = product.price + getOptionsPrice();
  const totalPrice = (unitPrice * quantity).toFixed(2);
  const originalUnitPrice = calculateOriginalPrice(unitPrice, product?._id || product?.id);
  const totalOriginalPrice = originalUnitPrice ? (originalUnitPrice * quantity).toFixed(2) : null;
  const canConfirm = areRequiredOptionsSelected();

  // Resolve indexed selections to a flat extras array of { extraId, name, price, quantity }.
  // The backend only consumes extraId + quantity, but we keep name/price locally for UI
  // (cart list, OrderSummary) and for the subtotal calculation in CartContext.
  const buildExtras = () => {
    const extras = [];
    Object.entries(selectedOptions).forEach(([groupIdx, value]) => {
      const group = optionGroups[groupIdx];
      if (!group) return;
      const indices = Array.isArray(value) ? value : [value];
      indices.forEach((optIdx) => {
        const opt = group.options?.[optIdx];
        if (opt && opt._id) {
          extras.push({
            extraId: opt._id,
            name: opt.name,
            price: opt.price || 0,
            quantity: 1,
          });
        }
      });
    });
    return extras;
  };

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm(product, quantity, buildExtras());
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-[60] flex flex-col items-center justify-end pointer-events-auto"
            onClick={onClose}
          >

            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-gray-50 rounded-t-[2rem] h-[60vh] flex flex-col pointer-events-auto"
            >

              <div className="px-4 pt-4 pb-3 bg-white rounded-t-[2rem] flex items-center gap-3">
                {product.image && (
                  <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                    <img
                      src={product.image.startsWith('http') ? product.image : `https://${product.image}`}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <h2 className="text-base font-bold font-display tracking-tight text-gray-900 truncate">
                  {product.name}
                </h2>
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-4">
                {optionGroups.map((group, groupIndex) => {
                  const maxSel = group.maxSelections || 1;
                  const isMulti = maxSel > 1;

                  return (
                    <div key={groupIndex} className="mb-5">
                      <h3 className="text-base font-bold text-gray-900 mb-0.5">
                        {group.title}
                      </h3>

                      <p className="text-xs text-gray-500 mb-3">
                        {isMulti
                          ? `Selecciona hasta ${maxSel}`
                          : group.required
                            ? 'Selecciona 1 (requerido)'
                            : 'Selecciona 1'}
                      </p>

                      <div className="bg-white rounded-2xl overflow-hidden">
                        {group.options.map((option, optionIndex) => {
                          const selected = isOptionSelected(groupIndex, optionIndex);
                          const unavailable = option.available === false;
                          const isLast = optionIndex === group.options.length - 1;

                          return (
                            <button
                              key={optionIndex}
                              onClick={() => !unavailable && handleOptionToggle(groupIndex, optionIndex, maxSel)}
                              disabled={unavailable}
                              className={`w-full flex items-center gap-3 px-4 py-3.5 ${!isLast ? 'border-b border-gray-100' : ''} ${unavailable ? 'opacity-50' : 'active:bg-gray-50'} transition-colors`}
                            >
                              <div
                                className="flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center"
                                style={{
                                  borderColor: selected ? 'var(--color-product-btn-stroke)' : '#D1D5DB',
                                }}
                              >
                                {selected && (
                                  <div
                                    className="w-2.5 h-2.5 rounded-full"
                                    style={{ backgroundColor: 'var(--color-product-btn-stroke)' }}
                                  />
                                )}
                              </div>

                              <span className="flex-1 text-left text-sm font-medium text-gray-900">
                                {option.name}
                              </span>

                              {option.price > 0 && (
                                <span className="text-sm text-gray-600 flex-shrink-0">
                                  +${option.price}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="px-4 py-4 bg-white border-t border-gray-100 flex items-center gap-3">

                {/* Stepper: pill con border gradient + contenido verde */}
                <div
                  className="relative rounded-full p-[2px] flex-shrink-0"
                  style={{
                    background: 'linear-gradient(to top, var(--color-product-btn-ring-from), var(--color-product-btn-ring-to))'
                  }}
                >
                  <div
                    className="flex items-center px-3 py-1.5 gap-3 rounded-full"
                    style={{
                      background: 'linear-gradient(to top, var(--color-secondary-100) 0%, #ffffff 100%)'
                    }}
                  >
                    <button
                      onClick={() => setQuantity(prev => Math.max(1, prev - 1))}
                      disabled={quantity <= 1}
                      className="flex items-center justify-center active:scale-90 transition-transform disabled:opacity-40 text-product-btn-stroke"
                      aria-label="Disminuir"
                    >
                      <svg width="16" height="4" viewBox="0 0 16 4" fill="none">
                        <path d="M2 2H14" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                      </svg>
                    </button>
                    <span className="text-base font-bold text-product-btn-stroke min-w-[20px] text-center">
                      {quantity}
                    </span>
                    <button
                      onClick={() => setQuantity(prev => prev + 1)}
                      className="flex items-center justify-center active:scale-90 transition-transform text-product-btn-stroke"
                      aria-label="Aumentar"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M8 2V14M2 8H14" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* CTA Agregar con tachado */}
                <button
                  onClick={handleConfirm}
                  disabled={!canConfirm}
                  className={`flex-1 py-3.5 rounded-full text-base font-bold active:scale-[0.98] transition-transform ${
                    canConfirm
                      ? 'bg-brand-primary text-white'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <span className="inline-flex items-center gap-2 justify-center">
                    <span>Agregar</span>
                    <span className="opacity-60">|</span>
                    <span>${totalPrice}</span>
                    {totalOriginalPrice && (
                      <span className="text-sm line-through opacity-70">
                        ${totalOriginalPrice}
                      </span>
                    )}
                  </span>
                </button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
